import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { CalendarEvent, TeamMember } from '../../types/types';

// Query response types
export interface GetTeamResponse { getTeam: TeamMember[] }
export interface GetTeamFromManagerOrgResponse { getTeamFromManagerOrg: TeamMember[] }
export interface GetCalendarForMinterAndOrgResponse { getCalendarForMinterAndOrg: CalendarEvent[] }
export interface GetCalendarsForMinterResponse { getCalendarsForMinter: CalendarEvent[] }

const addTeamMemberMutation = gql`
    mutation addTeamMember($ManagerUserName: String!, $Email: String!, $OrganizationId: String!) {
        addTeamMember(
            ManagerUserName: $ManagerUserName,
            Email: $Email,
            OrganizationId: $OrganizationId) {
            ManagerUserName
            Email
            OrganizationId
        }
    }
`;

const deleteTeamMemberMutation = gql`
    mutation deleteTeamMember($ManagerUserName: String!, $Email: String!, $OrganizationId: String!) {
        deleteTeamMember(
            ManagerUserName: $ManagerUserName,
            Email: $Email,
            OrganizationId: $OrganizationId) {
            ManagerUserName
            Email
            OrganizationId
        }
    }
`;

const addEventMutation = gql`
    mutation addEvent(
        $OwnerUserName: String!,
        $EventId: String!,
        $EventDate: String!,
        $OrganizationId: String!,
        $EventName: String!,
        $TargetEmail: String!,
        $Recurrence: String!) {
        addEvent(
            OwnerUserName: $OwnerUserName,
            EventId: $EventId,
            EventDate: $EventDate,
            OrganizationId: $OrganizationId,
            EventName: $EventName,
            TargetEmail: $TargetEmail,
            Recurrence: $Recurrence) {
            OwnerUserName
            EventId
            EventDate
            OrganizationId
            EventName
            TargetEmail
            Recurrence
        }
    }
`;

const deleteEventMutation = gql`
    mutation deleteEvent(
        $OwnerUserName: String!,
        $EventId: String!) {
        deleteEvent(
            OwnerUserName: $OwnerUserName,
            EventId: $EventId) {
            OwnerUserName
            EventId
        }
    }
`;

const teamQuery = gql`
    query getTeam($ManagerUserName: String!) {
        getTeam(ManagerUserName: $ManagerUserName) {
            ManagerUserName
            Email
            OrganizationId
            Organization {
                OrganizationId
                OrganizationName
            }
            User {
                UserName
                GivenName
                FamilyName
            }
            MostRecentAward {
                AwardId
                AwardName
                Accepted
                AcceptDate
                GrantDate
                NFTPermalink
                NFTLocation
            }
        }
    }
`;

function useTeamQuery(managerUserName: string) {
    return useQuery<GetTeamResponse>(
        teamQuery,
        {
            variables: {
                ManagerUserName: managerUserName,
            },
            notifyOnNetworkStatusChange: true,
            errorPolicy: 'all',
        },
    );
}

const teamFromManagerOrgQuery = gql`
    query getTeamFromManagerOrg(
        $ManagerUserName: String!,
        $OrganizationId: String!) {
        getTeamFromManagerOrg(
            ManagerUserName: $ManagerUserName,
            OrganizationId: $OrganizationId,
        ) {
            ManagerUserName
            Email
            OrganizationId
            User {
                UserName
                GivenName
                FamilyName
            }

        }
    }
`;

function useTeamFromManagerOrgQuery(managerUserName: string, organizationId: string) {
    return useQuery<GetTeamFromManagerOrgResponse>(
        teamFromManagerOrgQuery,
        {
            variables: {
                ManagerUserName: managerUserName,
                OrganizationId: organizationId,
            },
        },
    );
}

const getCalendarForMinterAndOrg = gql`
    query getCalendarForMinterAndOrg(
        $OwnerUserName: String!, $OrganizationId: String!) {
        getCalendarForMinterAndOrg(
            OwnerUserName: $OwnerUserName, OrganizationId: $OrganizationId) {
            OwnerUserName
            EventId
            OrganizationId
            EventName
            EventDate
            TargetEmail
            Recurrence
            Target {
                UserName
                GivenName
                FamilyName
            }
            Organization {
                OrganizationId
                OrganizationName
            }
        }
    }
`;

function useCalendarForMinterAndOrgQuery(ownerUserName: string, organizationId: string) {
    return useQuery<GetCalendarForMinterAndOrgResponse>(
        getCalendarForMinterAndOrg, {
            variables: {
                OwnerUserName: ownerUserName,
                OrganizationId: organizationId,
            },
        },
    );
}

const getCalendarsForMinter = gql`
    query getCalendarsForMinter(
        $OwnerUserName: String!) {
        getCalendarsForMinter(
            OwnerUserName: $OwnerUserName) {
            OwnerUserName
            EventId
            OrganizationId
            EventName
            EventDate
            TargetEmail
            Recurrence
            Target {
                UserName
                GivenName
                FamilyName
            }
            Organization {
                OrganizationId
                OrganizationName
            }
        }
    }
`;

function useCalendarsForMinterQuery(ownerUserName: string) {
    return useQuery<GetCalendarsForMinterResponse>(
        getCalendarsForMinter, {
            variables: {
                OwnerUserName: ownerUserName,
            },
        },
    );
}

export {
    teamQuery,
    teamFromManagerOrgQuery,
    getCalendarForMinterAndOrg,
    getCalendarsForMinter,
    addTeamMemberMutation,
    deleteTeamMemberMutation,
    addEventMutation,
    deleteEventMutation,
    useTeamQuery,
    useTeamFromManagerOrgQuery,
    useCalendarForMinterAndOrgQuery,
    useCalendarsForMinterQuery,
};
